import apiClient from './apiClient';

// Image compression utility - reduces image size before upload
const compressImage = (file, maxSizeKB = 1024, quality = 0.85) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      const maxDimension = 1600; // Preserve more detail unless image is extremely large
      
      if (width > height) {
        if (width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try different quality levels until size is acceptable
      let currentQuality = quality;
      let compressedDataUrl;
      
      const tryCompress = () => {
        compressedDataUrl = canvas.toDataURL('image/jpeg', currentQuality);
        
        // Calculate size in KB
        const sizeKB = Math.round((compressedDataUrl.length * 3) / 4 / 1024);
        
        if (sizeKB > maxSizeKB && currentQuality > 0.4) {
          currentQuality -= 0.05;
          tryCompress();
        } else {
          resolve({
            dataUrl: compressedDataUrl,
            sizeKB,
            originalSizeKB: Math.round(file.size / 1024)
          });
        }
      };
      
      tryCompress();
    };
    
    img.src = URL.createObjectURL(file);
  });
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Convert file to base64 with light compression only when necessary
export const fileToBase64 = async (file, maxSizeKB = 1024) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  try {
    const fileSizeKB = Math.round(file.size / 1024);
    if (fileSizeKB <= maxSizeKB) {
      return await readFileAsDataUrl(file);
    }

    const compressed = await compressImage(file, maxSizeKB);
    console.log(`Image compressed: ${compressed.originalSizeKB}KB → ${compressed.sizeKB}KB`);
    return compressed.dataUrl;
  } catch (error) {
    console.error('Error compressing image:', error);
    throw error;
  }
};

// Questionnaire API service
const questionnaireService = {
  
  // Get all questionnaires (admin view)
  getAll: async (params = {}) => {
    const { fetchAll = false, ...queryParams } = params;

    if (!fetchAll) {
      const response = await apiClient.get('/questionnaires', { params: queryParams });
      return response.data;
    }

    const pageSize = queryParams.limit || 100;
    let currentPage = 1;
    let totalPages = 1;
    const allQuestionnaires = [];
    let lastResponseData = null;

    do {
      const response = await apiClient.get('/questionnaires', {
        params: {
          ...queryParams,
          page: currentPage,
          limit: pageSize
        }
      });

      lastResponseData = response.data;

      if (!lastResponseData?.success) {
        return lastResponseData;
      }

      allQuestionnaires.push(...(lastResponseData.data || []));
      totalPages = lastResponseData.pagination?.totalPages || 1;
      currentPage += 1;
    } while (currentPage <= totalPages);

    return {
      ...lastResponseData,
      data: allQuestionnaires,
      pagination: {
        ...(lastResponseData?.pagination || {}),
        currentPage: totalPages,
        itemsPerPage: pageSize,
        totalItems: allQuestionnaires.length,
        totalPages
      }
    };
  },

  // Get single questionnaire (admin view with correct answers)
  getById: async (id) => {
    const response = await apiClient.get(`/questionnaires/${id}`);
    return response.data;
  },

  // Create new questionnaire
  create: async (questionnaireData) => {
    const response = await apiClient.post('/questionnaires', questionnaireData);
    return response.data;
  },

  // Update questionnaire
  update: async (id, questionnaireData) => {
    try {
      const response = await apiClient.put(`/questionnaires/${id}`, questionnaireData);
      return response.data;
    } catch (err) {
      if (err.response?.status === 409) {
        return { success: false, conflict: true, ...err.response.data };
      }
      throw err;
    }
  },

  // Draft save (auto-save with relaxed validation)
  draftSave: async (id, questionnaireData) => {
    try {
      if (id) {
        const response = await apiClient.patch(`/questionnaires/${id}/draft`, questionnaireData, { timeout: 15000 });
        return response.data;
      } else {
        const response = await apiClient.post('/questionnaires/draft', questionnaireData, { timeout: 15000 });
        return response.data;
      }
    } catch (err) {
      if (err.response?.status === 409) {
        return { success: false, conflict: true, ...err.response.data };
      }
      throw err;
    }
  },

  // Presence: ping while editing, leave on unmount
  presencePing: async (id) => {
    try {
      const response = await apiClient.post(`/questionnaires/${id}/presence/ping`);
      return response.data;
    } catch {
      return { success: false, editors: [] };
    }
  },

  presenceLeave: async (id) => {
    try {
      await apiClient.post(`/questionnaires/${id}/presence/leave`);
    } catch { /* silent */ }
  },

  // Delete questionnaire
  delete: async (id) => {
    const response = await apiClient.delete(`/questionnaires/${id}`);
    return response.data;
  },

  // Publish/unpublish/archive questionnaire
  updateStatus: async (id, action) => {
    const response = await apiClient.patch(`/questionnaires/${id}/status`, { action });
    return response.data;
  },

  // Toggle publish status (complete, publish, unpublish, archive)
  togglePublish: async (id, action) => {
    const response = await apiClient.patch(`/questionnaires/${id}/status`, { action });
    return response.data;
  },

  // Complete a questionnaire (draft -> complete)
  complete: async (id) => {
    const response = await apiClient.patch(`/questionnaires/${id}/complete`);
    return response.data;
  },

  // Archive a questionnaire
  archive: async (id) => {
    const response = await apiClient.patch(`/questionnaires/${id}/archive`);
    return response.data;
  },

  // PUBLIC ROUTES (for taking quizzes)
  
  // Get questionnaire for taking (public, no correct answers)
  getForTaking: async (id) => {
    const response = await apiClient.get(`/questionnaires/${id}/take`);
    return response.data;
  },

  // Submit questionnaire answers
  submit: async (id, answers) => {
    const response = await apiClient.post(`/questionnaires/${id}/submit`, { answers });
    return response.data;
  },

  // Parse raw quiz text into structured questions via AI agent
  parseQuizText: async (text) => {
    try {
      const response = await apiClient.post('/questionnaires/parse-ai', { text }, { timeout: 90000 });
      return response.data;
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to parse quiz text' };
    }
  },
};

export default questionnaireService;